import React from "react";
import { useIntl } from "react-intl";

import { Panel } from "../../../resusableComponents/funnelFuel";

import { useFilters } from "../lib/FilterContext";
import { fmt, fnColor } from "../lib/selectors";

const AssetRow = ({ model, asset, maxCount, hasFunctions, active }) => {
  const intl = useIntl();
  const { toggle, isSelected } = useFilters();
  const pressed = isSelected(asset.selection);

  return (
    <div className="ff-asset-row" data-muted={(active && !asset.count) || undefined}>
      <button
        type="button"
        className="ff-asset-btn"
        aria-pressed={pressed}
        title={intl.formatMessage({ id: "ff_dashboard.assets.filter_asset", defaultMessage: "Filter by asset: {name}" }, { name: asset.label })}
        onClick={() => toggle(asset.selection)}
      >
        {asset.label}
      </button>

      <div className="ff-asset-bar">
        <div className="ff-stack" style={{ width: `${Math.max(1, (asset.count / maxCount) * 100)}%` }}>
          {hasFunctions
            ? asset.segments.map((segment) => (segment.selection === null
              // Unknown job function: shown, but there is no value to filter on.
              ? (
                <span
                  key="unknown"
                  className="ff-segment ff-static"
                  role="img"
                  aria-label={intl.formatMessage({ id: "ff_dashboard.assets.unknown_function", defaultMessage: "Unknown job function" }) + `, ${segment.count}`}
                  title={intl.formatMessage({ id: "ff_dashboard.assets.unknown_function", defaultMessage: "Unknown job function" }) + ` · ${segment.count}`}
                  style={{ width: `${(segment.count / asset.count) * 100}%`, background: fnColor(model, "") }}
                />
              )
              : (
                <button
                  key={segment.jobFunctionKey}
                  type="button"
                  className="ff-segment"
                  aria-pressed={isSelected(segment.selection)}
                  aria-label={intl.formatMessage(
                    { id: "ff_dashboard.assets.filter_function", defaultMessage: "Filter by job function {function}, {count} leads on {asset}" },
                    { function: segment.label, count: segment.count, asset: asset.label }
                  )}
                  title={intl.formatMessage(
                    { id: "ff_dashboard.assets.segment_title", defaultMessage: "{asset} · {function} · {count} leads" },
                    { asset: asset.label, function: segment.label, count: segment.count }
                  )}
                  onClick={() => toggle(segment.selection)}
                  // Colour by NAME. An index into a palette array repaints every other function the moment one drops out.
                  style={{ width: `${(segment.count / asset.count) * 100}%`, background: fnColor(model, segment.label) }}
                />
              )))
            : (asset.count > 0 && <span className="ff-segment ff-solid" style={{ width: "100%" }} />)}
        </div>
      </div>

      <span className="ff-bar-val ff-strong">{fmt(asset.count)}</span>
      <span className="ff-bar-val ff-muted">{asset.share}%</span>
      {hasFunctions && (
        <span className="ff-strongest" style={{ color: asset.strongest ? fnColor(model, asset.strongest.label) : undefined }}>
          {asset.strongest ? `${asset.strongest.label} ${asset.strongest.percentage}%` : "—"}
        </span>
      )}
    </div>
  );
};

/* Assets, and who they reach. Stacked bars are flex buttons, not a chart library. */
export const AssetsPanel = ({ view, model }) => {
  const intl = useIntl();
  const { active } = useFilters();
  // Without the job function field there is nothing to stack or colour: plain bars, no legend.
  const hasFunctions = model.capabilities.jobFunction === true;

  return (
    <Panel
      title={intl.formatMessage({ id: "ff_dashboard.assets.title", defaultMessage: "Assets, and who they reach" })}
      caption={intl.formatMessage({ id: "ff_dashboard.assets.caption", defaultMessage: "Bar length is leads; colour is the job function behind them" })}
      isEmpty={view.assets.rows.length === 0}
      emptyMessage={intl.formatMessage({ id: "ff_dashboard.empty.selection", defaultMessage: "Nothing in this selection." })}
      actions={hasFunctions && (
        <ul className="ff-legend">
          {model.jobFunctionOrder.map((fn) => (
            <li key={fn}><span className="ff-swatch" style={{ background: fnColor(model, fn) }} />{fn}</li>
          ))}
        </ul>
      )}
    >
      <div className="ff-asset-list">
        {view.assets.rows.map((asset) => (
          <AssetRow key={asset.key} model={model} asset={asset} maxCount={view.assets.max} hasFunctions={hasFunctions} active={active} />
        ))}
      </div>
    </Panel>
  );
};
